// 비밀번호 찾기 / 재설정 / 이메일 인증 (IMP-6f)
import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { authApi } from "@taplink/web-shared";
import { ApiError } from "@taplink/web-shared";

/** 재설정 메일 요청 — 계정 존재 여부를 노출하지 않으므로 항상 동일한 안내를 보여준다 */
export function ForgotPasswordPage() {
  const [loginId, setLoginId] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await authApi.requestPasswordReset(loginId);
      setSent(true);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "요청에 실패했습니다.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="page">
      <div className="card">
        <h1>비밀번호 찾기</h1>
        <p className="subtitle">가입한 이메일로 재설정 링크를 보내드립니다.</p>

        {error && <div className="alert alert-error" role="alert">{error}</div>}

        {sent ? (
          <>
            {/* '보냈다'는 완료다 — 안내(alert-info)가 아니라 성공으로 알린다 */}
            <div className="alert alert-success" role="status">
              해당 계정이 존재한다면 재설정 안내 메일을 발송했습니다. 메일함을 확인해 주세요.
            </div>
            <Link to="/login">로그인으로 돌아가기</Link>
          </>
        ) : (
          <form onSubmit={onSubmit}>
            <div className="field">
              <label htmlFor="loginId">이메일</label>
              <input
                id="loginId"
                type="email"
                autoComplete="username"
                required
                value={loginId}
                onChange={(e) => setLoginId(e.target.value)}
              />
            </div>
            <button type="submit" className="btn-primary btn-block" disabled={submitting}>
              {submitting ? "전송 중…" : "재설정 링크 받기"}
            </button>
            <div className="footer-links">
              <Link to="/login">로그인</Link>
            </div>
          </form>
        )}
      </div>
    </main>
  );
}

/** 메일 링크로 진입 — 새 비밀번호 설정 */
export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") ?? "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (password !== confirm) {
      setError("비밀번호가 일치하지 않습니다.");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await authApi.resetPassword(token, password);
      navigate("/login", { replace: true });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "재설정에 실패했습니다.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="page">
      <div className="card">
        <h1>새 비밀번호 설정</h1>
        <p className="subtitle">사용할 새 비밀번호를 입력해 주세요.</p>

        {!token && <div className="alert alert-error" role="alert">유효하지 않은 접근입니다.</div>}
        {error && <div className="alert alert-error" role="alert">{error}</div>}

        <form onSubmit={onSubmit}>
          <div className="field">
            <label htmlFor="password">새 비밀번호</label>
            <input
              id="password" type="password" autoComplete="new-password" required minLength={8}
              value={password} onChange={(e) => setPassword(e.target.value)}
              aria-describedby="pw-hint"
            />
            <p className="hint" id="pw-hint">최소 8자 이상</p>
          </div>
          <div className="field">
            <label htmlFor="confirm">새 비밀번호 확인</label>
            <input
              id="confirm" type="password" autoComplete="new-password" required
              value={confirm} onChange={(e) => setConfirm(e.target.value)}
            />
          </div>
          <button type="submit" className="btn-primary btn-block" disabled={!token || submitting}>
            {submitting ? "변경 중…" : "비밀번호 변경"}
          </button>
        </form>
      </div>
    </main>
  );
}

/** 메일 인증 링크로 진입 */
export function VerifyEmailPage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [state, setState] = useState<"loading" | "done" | "error">("loading");
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!token) {
      setState("error");
      setMessage("유효하지 않은 접근입니다.");
      return;
    }
    authApi
      .verifyEmail(token)
      .then(() => setState("done"))
      .catch((e: unknown) => {
        setState("error");
        setMessage(e instanceof ApiError ? e.message : "인증에 실패했습니다.");
      });
  }, [token]);

  return (
    <main className="page">
      <div className="card">
        <h1>이메일 인증</h1>
        {state === "loading" && <p className="subtitle" role="status">확인 중…</p>}
        {state === "done" && (
          <>
            <div className="alert alert-success" role="status">이메일 인증이 완료되었습니다.</div>
            <Link to="/login">로그인하기</Link>
          </>
        )}
        {state === "error" && (
          <>
            <div className="alert alert-error" role="alert">{message}</div>
            <Link to="/login">로그인으로 돌아가기</Link>
          </>
        )}
      </div>
    </main>
  );
}
